import { BigNumber } from 'ethers'
import { BNToNumberWithDecimals, ChainId, Network, getNetworkFromChainId, stringToBigNumber } from '@mprolab/sdk'
import type { MetaToken } from 'types/metaToken'
import { getMulticallResponse } from './getMulticallResponse'
import { getSimpleRpcProviderByChainId } from './providers'
import { getUniswapPairContract } from './contractHelpers'
import { logMessage } from './logMessage'
import { resolveErc20MetaToken } from './resolveErc20MetaToken'
import { sanitizeMetaToken } from './tokenLogoUri'

function networkForChain(chainId: number): Network {
  return getNetworkFromChainId(chainId as ChainId) ?? Network.BASE
}

export function lpPairMetaToken(pairAddress: string, chainId: number): MetaToken {
  const addr = pairAddress.toLowerCase()
  return sanitizeMetaToken({
    address: addr,
    token: 'LP',
    tokenName: 'Uniswap V2 LP',
    decimals: 18,
    network: networkForChain(chainId),
    logoURI: '',
    chainId,
  })
}

async function isUniswapV2PairAddress(tokenAddress: string, chainId: number): Promise<boolean> {
  try {
    const provider = getSimpleRpcProviderByChainId(chainId)
    const pair = getUniswapPairContract(tokenAddress, provider, chainId)
    await Promise.all([pair.token0(), pair.token1()])
    return true
  } catch {
    return false
  }
}

export async function resolveClassicStakeTokenDetails(
  stakeToken: string,
  rewardToken: string,
  chainId: number,
  getMproPrice: (amount: BigNumber, chainId: number, removeDust?: boolean) => Promise<number | null | 0>,
  getTokenPrice: (
    tokenAddress: string,
    amount: BigNumber,
    chainId: number,
    removeDust?: boolean,
  ) => Promise<number | null | 0>,
): Promise<{
  stakeTokenMeta: MetaToken
  rewardTokenMeta: MetaToken
  token0?: MetaToken
  token1?: MetaToken
  actualStakeTokenPrice: number | null | 0
  actualRewardTokenPrice: number | null | 0
}> {
  const rewardTokenMeta = await resolveErc20MetaToken(rewardToken, chainId)
  const ONE_TOKEN = BigNumber.from(1).mul(BigNumber.from(10).pow(18))
  let actualStakeTokenPrice = await getMproPrice(ONE_TOKEN, chainId)
  let actualRewardTokenPrice = actualStakeTokenPrice

  let token0: MetaToken | undefined
  let token1: MetaToken | undefined
  let stakeTokenMeta: MetaToken

  const isPair = await isUniswapV2PairAddress(stakeToken, chainId)

  if (isPair) {
    try {
      const provider = getSimpleRpcProviderByChainId(chainId)
      const uniswapPairContract = getUniswapPairContract(stakeToken, provider, chainId)
      const [token0AddressRes, token1AddressRes, getReservesRes, totalSupplyRes] = await getMulticallResponse({
        chainId,
        calls: [
          {
            target: uniswapPairContract.address,
            callData: uniswapPairContract.interface.encodeFunctionData('token0'),
            allowFailure: false,
          },
          {
            target: uniswapPairContract.address,
            callData: uniswapPairContract.interface.encodeFunctionData('token1'),
            allowFailure: false,
          },
          {
            target: uniswapPairContract.address,
            callData: uniswapPairContract.interface.encodeFunctionData('getReserves'),
            allowFailure: false,
          },
          {
            target: uniswapPairContract.address,
            callData: uniswapPairContract.interface.encodeFunctionData('totalSupply'),
            allowFailure: false,
          },
        ],
      })
      const [token0Address] = uniswapPairContract.interface.decodeFunctionResult(
        'token0',
        token0AddressRes.returnData,
      )
      const [token1Address] = uniswapPairContract.interface.decodeFunctionResult(
        'token1',
        token1AddressRes.returnData,
      )
      const [reserve0, reserve1] = uniswapPairContract.interface.decodeFunctionResult(
        'getReserves',
        getReservesRes.returnData,
      )
      const [totalSupply] = uniswapPairContract.interface.decodeFunctionResult(
        'totalSupply',
        totalSupplyRes.returnData,
      )
      const token0PriceUSD = await getTokenPrice(token0Address, ONE_TOKEN, chainId)
      const token1PriceUSD = await getTokenPrice(token1Address, ONE_TOKEN, chainId)
      const token0PriceBigNumber = stringToBigNumber(token0PriceUSD?.toString() || '', 6)
      const token1PriceBigNumber = stringToBigNumber(token1PriceUSD?.toString() || '', 6)

      const totalPairValue = reserve0.mul(token0PriceBigNumber).add(reserve1.mul(token1PriceBigNumber))
      const singleLPPriceUSD = totalPairValue.div(totalSupply)
      actualStakeTokenPrice = BNToNumberWithDecimals(singleLPPriceUSD, 6)
      actualRewardTokenPrice = actualStakeTokenPrice

      token0 = await resolveErc20MetaToken(token0Address, chainId)
      token1 = await resolveErc20MetaToken(token1Address, chainId)
      stakeTokenMeta = lpPairMetaToken(stakeToken, chainId)
    } catch (error) {
      logMessage(error, 'Check if stake token is uniswap pair')
      stakeTokenMeta = await resolveErc20MetaToken(stakeToken, chainId)
    }
  } else {
    stakeTokenMeta = await resolveErc20MetaToken(stakeToken, chainId)
  }

  return {
    stakeTokenMeta,
    rewardTokenMeta,
    token0,
    token1,
    actualStakeTokenPrice,
    actualRewardTokenPrice,
  }
}
